import { v4 as uuidv4 } from 'uuid';
import { createDefaultPreferences, createDefaultStats, habitInstanceKeysFor, deletionEffect } from '@trackmind/core';
/**
 * In-memory storage implementation for testing
 */
export class MemoryStorage {
    tasks = new Map();
    projects = new Map();
    schedules = new Map();
    goals = new Map();
    notes = new Map();
    lists = new Map();
    listInstances = new Map();
    habitInstances = new Map();
    journalEntries = new Map();
    reviews = new Map();
    fitnessWorkouts = new Map();
    fitnessBodyweights = new Map();
    fitnessSettings = null;
    moodCheckIns = new Map();
    preferences = createDefaultPreferences();
    stats = createDefaultStats();
    pendingChanges = new Map();
    tombstones = new Map();
    lastSyncTime = null;
    async initialize() {
        // No initialization needed for memory storage
    }
    async close() {
        // No cleanup needed
    }
    // Tasks
    async getTasks() {
        return Array.from(this.tasks.values());
    }
    async getTask(id) {
        return this.tasks.get(id) ?? null;
    }
    async getTasksByProject(projectId) {
        return Array.from(this.tasks.values()).filter(t => t.project_id === projectId);
    }
    async getTasksByStatus(status) {
        return Array.from(this.tasks.values()).filter(t => t.status === status);
    }
    async saveTask(task) {
        const existing = this.tasks.has(task.id);
        this.tasks.set(task.id, task);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'task',
            operation: existing ? 'update' : 'create',
            entityId: task.id,
            timestamp: new Date().toISOString(),
        });
    }
    async saveTasks(tasks) {
        for (const task of tasks)
            await this.saveTask(task);
    }
    async deleteTask(id) {
        const now = new Date().toISOString();
        // The habit's completion history goes with it, as in IndexedDB.
        for (const key of habitInstanceKeysFor([...this.habitInstances.values()], [id])) {
            this.habitInstances.delete(key);
            await this.recordInstanceDeletion(key, now);
        }
        this.tasks.delete(id);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'task',
            operation: 'delete',
            entityId: id,
            timestamp: now,
        });
        await this.addTombstone({
            id: uuidv4(),
            entityType: 'task',
            entityId: id,
            deletedAt: now,
        });
    }
    // Projects
    async getProjects() {
        return Array.from(this.projects.values());
    }
    async getProject(id) {
        return this.projects.get(id) ?? null;
    }
    async saveProject(project) {
        const existed = this.projects.has(project.id);
        this.projects.set(project.id, project);
        await this.track('project', project.id, existed);
    }
    async deleteProject(id) {
        this.projects.delete(id);
        await this.recordDeletion('project', id);
    }
    // Schedules
    async getSchedule(date) {
        return this.schedules.get(date) ?? null;
    }
    async getSchedules(startDate, endDate) {
        return Array.from(this.schedules.values()).filter(s => s.date >= startDate && s.date <= endDate);
    }
    async saveSchedule(schedule) {
        const existed = this.schedules.has(schedule.date);
        this.schedules.set(schedule.date, schedule);
        // A date is a reused key, so saving it again retires its own tombstone.
        this.clearTombstone('schedule', schedule.date);
        await this.track('schedule', schedule.date, existed);
    }
    async saveSchedules(schedules) {
        for (const schedule of schedules)
            await this.saveSchedule(schedule);
    }
    async deleteSchedule(date) {
        this.schedules.delete(date);
        await this.recordDeletion('schedule', date);
    }
    // Goals
    async getGoals() {
        return Array.from(this.goals.values());
    }
    async getGoal(id) {
        return this.goals.get(id) ?? null;
    }
    async saveGoal(goal) {
        const existed = this.goals.has(goal.id);
        this.goals.set(goal.id, goal);
        await this.track('goal', goal.id, existed);
    }
    async deleteGoal(id) {
        this.goals.delete(id);
        await this.recordDeletion('goal', id);
    }
    // Notes
    async getNotes() {
        return Array.from(this.notes.values());
    }
    async getNote(id) {
        return this.notes.get(id) ?? null;
    }
    async getNotesByFolder(folder) {
        return Array.from(this.notes.values()).filter(n => n.folder_path === folder);
    }
    async saveNote(note) {
        const existed = this.notes.has(note.id);
        this.notes.set(note.id, note);
        await this.track('note', note.id, existed);
    }
    async deleteNote(id) {
        this.notes.delete(id);
        await this.recordDeletion('note', id);
    }
    // Lists
    async getLists() {
        return Array.from(this.lists.values());
    }
    async getList(id) {
        return this.lists.get(id) ?? null;
    }
    async saveList(list) {
        const existed = this.lists.has(list.id);
        this.lists.set(list.id, list);
        await this.track('list', list.id, existed);
    }
    async deleteList(id) {
        this.lists.delete(id);
        await this.recordDeletion('list', id);
    }
    // List Instances
    async getListInstances() {
        return Array.from(this.listInstances.values());
    }
    async getListInstance(id) {
        return this.listInstances.get(id) ?? null;
    }
    async saveListInstance(instance) {
        const existed = this.listInstances.has(instance.id);
        this.listInstances.set(instance.id, instance);
        await this.track('list_instance', instance.id, existed);
    }
    async deleteListInstance(id) {
        this.listInstances.delete(id);
        await this.recordDeletion('list_instance', id);
    }
    // Habit Instances
    async getHabitInstance(taskId, date) {
        const key = `${taskId}:${date}`;
        return this.habitInstances.get(key) ?? null;
    }
    async getHabitInstances(date) {
        return Array.from(this.habitInstances.values()).filter(hi => hi.date === date);
    }
    async getHabitInstancesByHabit(habitId, startDate, endDate) {
        let results = Array.from(this.habitInstances.values()).filter(hi => hi.habit_id === habitId);
        if (startDate) {
            results = results.filter(r => r.date >= startDate);
        }
        if (endDate) {
            results = results.filter(r => r.date <= endDate);
        }
        return results;
    }
    async getAllHabitInstances() {
        return Array.from(this.habitInstances.values());
    }
    async saveHabitInstance(instance) {
        const key = `${instance.habit_id}:${instance.date}`;
        const existed = this.habitInstances.has(key);
        this.habitInstances.set(key, instance);
        // Re-ticked after an untick: the key is reused, so its tombstone retires.
        this.clearTombstone('habit_instance', key);
        await this.track('habit_instance', key, existed);
    }
    async deleteHabitInstance(habitId, date) {
        const key = `${habitId}:${date}`;
        this.habitInstances.delete(key);
        // Tombstoned like IndexedDB does, so the sync tests (which run on this
        // adapter) see the same deletion an app user's device produces.
        await this.recordInstanceDeletion(key, new Date().toISOString());
    }
    async recordInstanceDeletion(key, now) {
        await this.addPendingChange({ id: uuidv4(), type: 'habit_instance', operation: 'delete', entityId: key, timestamp: now });
        await this.addTombstone({ id: uuidv4(), entityType: 'habit_instance', entityId: key, deletedAt: now });
    }
    // Journal Entries
    async getJournalEntries() {
        return Array.from(this.journalEntries.values());
    }
    async getJournalEntry(id) {
        return this.journalEntries.get(id) ?? null;
    }
    async getJournalEntriesByDate(date) {
        return Array.from(this.journalEntries.values()).filter(e => e.date === date);
    }
    async getJournalEntriesByDateRange(startDate, endDate) {
        return Array.from(this.journalEntries.values()).filter(e => e.date >= startDate && e.date <= endDate);
    }
    async saveJournalEntry(entry) {
        const existing = this.journalEntries.has(entry.id);
        this.journalEntries.set(entry.id, entry);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'journal',
            operation: existing ? 'update' : 'create',
            entityId: entry.id,
            timestamp: new Date().toISOString(),
        });
    }
    // Reviews
    async getReviews() {
        return Array.from(this.reviews.values());
    }
    async saveReview(review) {
        const existing = this.reviews.has(review.id);
        this.reviews.set(review.id, review);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'review',
            operation: existing ? 'update' : 'create',
            entityId: review.id,
            timestamp: new Date().toISOString(),
        });
    }
    async deleteReview(id) {
        this.reviews.delete(id);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'review',
            operation: 'delete',
            entityId: id,
            timestamp: new Date().toISOString(),
        });
    }
    // Fitness
    async getFitnessWorkouts() {
        return Array.from(this.fitnessWorkouts.values());
    }
    async saveFitnessWorkout(workout) {
        const existing = this.fitnessWorkouts.has(workout.id);
        this.fitnessWorkouts.set(workout.id, workout);
        await this.addPendingChange({
            id: uuidv4(), type: 'fitness_workout', operation: existing ? 'update' : 'create',
            entityId: workout.id, timestamp: new Date().toISOString(),
        });
    }
    async deleteFitnessWorkout(id) {
        this.fitnessWorkouts.delete(id);
        await this.recordFitnessDeletion('fitness_workout', id);
    }
    async getFitnessBodyweights() {
        return Array.from(this.fitnessBodyweights.values());
    }
    async saveFitnessBodyweight(entry) {
        const existing = this.fitnessBodyweights.has(entry.id);
        this.fitnessBodyweights.set(entry.id, entry);
        this.clearTombstone('fitness_bodyweight', entry.id);
        await this.addPendingChange({
            id: uuidv4(), type: 'fitness_bodyweight', operation: existing ? 'update' : 'create',
            entityId: entry.id, timestamp: new Date().toISOString(),
        });
    }
    async deleteFitnessBodyweight(id) {
        this.fitnessBodyweights.delete(id);
        await this.recordFitnessDeletion('fitness_bodyweight', id);
    }
    async getFitnessSettings() {
        return this.fitnessSettings;
    }
    async saveFitnessSettings(settings) {
        const existing = this.fitnessSettings !== null;
        this.fitnessSettings = settings;
        await this.addPendingChange({
            id: uuidv4(), type: 'fitness_settings', operation: existing ? 'update' : 'create',
            entityId: settings.id, timestamp: new Date().toISOString(),
        });
    }
    async recordFitnessDeletion(type, id) {
        const now = new Date().toISOString();
        await this.addPendingChange({ id: uuidv4(), type, operation: 'delete', entityId: id, timestamp: now });
        await this.addTombstone({ id: uuidv4(), entityType: type, entityId: id, deletedAt: now });
    }
    async deleteJournalEntry(id) {
        const now = new Date().toISOString();
        this.journalEntries.delete(id);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'journal',
            operation: 'delete',
            entityId: id,
            timestamp: now,
        });
        await this.addTombstone({
            id: uuidv4(),
            entityType: 'journal',
            entityId: id,
            deletedAt: now,
        });
    }
    // Mood Check-ins
    async getMoodCheckIns() {
        return Array.from(this.moodCheckIns.values());
    }
    async getMoodCheckIn(id) {
        return this.moodCheckIns.get(id) ?? null;
    }
    async getMoodCheckInsByDate(date) {
        return Array.from(this.moodCheckIns.values()).filter(m => m.date === date);
    }
    async getMoodCheckInsByDateRange(startDate, endDate) {
        return Array.from(this.moodCheckIns.values()).filter(m => m.date >= startDate && m.date <= endDate);
    }
    async saveMoodCheckIn(moodCheckIn) {
        const existing = this.moodCheckIns.has(moodCheckIn.id);
        this.moodCheckIns.set(moodCheckIn.id, moodCheckIn);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'mood_checkin',
            operation: existing ? 'update' : 'create',
            entityId: moodCheckIn.id,
            timestamp: new Date().toISOString(),
        });
    }
    async deleteMoodCheckIn(id) {
        const now = new Date().toISOString();
        this.moodCheckIns.delete(id);
        await this.addPendingChange({
            id: uuidv4(),
            type: 'mood_checkin',
            operation: 'delete',
            entityId: id,
            timestamp: now,
        });
        await this.addTombstone({
            id: uuidv4(),
            entityType: 'mood_checkin',
            entityId: id,
            deletedAt: now,
        });
    }
    // Preferences
    async getPreferences() {
        return { ...this.preferences };
    }
    async savePreferences(preferences) {
        this.preferences = { ...preferences };
        await this.track('preferences', 'default', true);
    }
    // Stats
    async getStats() {
        return { ...this.stats };
    }
    async saveStats(stats) {
        this.stats = { ...stats };
        await this.track('stats', 'default', true);
    }
    // Sync Metadata
    async getLastSyncTime() {
        return this.lastSyncTime;
    }
    async setLastSyncTime(time) {
        this.lastSyncTime = time;
    }
    async getPendingChanges() {
        return Array.from(this.pendingChanges.values()).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }
    async addPendingChange(change) {
        this.pendingChanges.set(change.id, change);
    }
    async clearPendingChanges(changeIds) {
        for (const id of changeIds) {
            this.pendingChanges.delete(id);
        }
    }
    // Tombstone Management
    async getTombstones() {
        return Array.from(this.tombstones.values());
    }
    /**
     * Parity with IndexedDB, which records a pending change for every save and
     * a tombstone for every delete. The sync tests run on this adapter; where it
     * skipped either, those tests could not see a sync bug IndexedDB would have.
     */
    async track(type, entityId, existed) {
        await this.addPendingChange({
            id: uuidv4(), type, operation: existed ? 'update' : 'create',
            entityId, timestamp: new Date().toISOString(),
        });
    }
    async recordDeletion(type, entityId) {
        const { tombstones, pendingChanges } = deletionEffect(type, [entityId], new Date().toISOString());
        for (const change of pendingChanges)
            await this.addPendingChange(change);
        for (const tombstone of tombstones)
            await this.addTombstone(tombstone);
    }
    clearTombstone(type, entityId) {
        for (const [id, t] of this.tombstones) {
            if (t.entityType === type && t.entityId === entityId)
                this.tombstones.delete(id);
        }
    }
    async addTombstone(tombstone) {
        this.tombstones.set(tombstone.id, tombstone);
    }
    async importTombstones(tombstones) {
        const existingKeys = new Set(Array.from(this.tombstones.values()).map(t => `${t.entityType}:${t.entityId}`));
        for (const t of tombstones) {
            const key = `${t.entityType}:${t.entityId}`;
            if (!existingKeys.has(key)) {
                this.tombstones.set(t.id, t);
            }
        }
    }
    async cleanupTombstones(olderThanDays) {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);
        const cutoffISO = cutoffDate.toISOString();
        const toDelete = [];
        for (const [id, t] of this.tombstones) {
            if (t.deletedAt < cutoffISO) {
                toDelete.push(id);
            }
        }
        for (const id of toDelete) {
            this.tombstones.delete(id);
        }
        return toDelete.length;
    }
    // Bulk Operations
    async exportAll() {
        return {
            version: '1.0.0',
            exportedAt: new Date().toISOString(),
            tasks: await this.getTasks(),
            projects: await this.getProjects(),
            schedules: Array.from(this.schedules.values()),
            goals: await this.getGoals(),
            notes: await this.getNotes(),
            lists: await this.getLists(),
            listInstances: await this.getListInstances(),
            habitInstances: Array.from(this.habitInstances.values()),
            journalEntries: await this.getJournalEntries(),
            moodCheckIns: await this.getMoodCheckIns(),
            reviews: await this.getReviews(),
            fitnessWorkouts: await this.getFitnessWorkouts(),
            fitnessBodyweights: await this.getFitnessBodyweights(),
            fitnessSettings: this.fitnessSettings ? [this.fitnessSettings] : [],
            preferences: await this.getPreferences(),
            stats: await this.getStats(),
            tombstones: {
                tombstones: await this.getTombstones(),
                cleanedUpBefore: undefined,
            },
        };
    }
    async importAll(data) {
        // Clear existing
        this.tasks.clear();
        this.projects.clear();
        this.schedules.clear();
        this.goals.clear();
        this.notes.clear();
        this.lists.clear();
        this.listInstances.clear();
        this.habitInstances.clear();
        this.journalEntries.clear();
        this.moodCheckIns.clear();
        this.reviews.clear();
        this.fitnessWorkouts.clear();
        this.fitnessBodyweights.clear();
        this.fitnessSettings = null;
        // Import
        for (const task of data.tasks)
            this.tasks.set(task.id, task);
        for (const project of data.projects)
            this.projects.set(project.id, project);
        for (const schedule of data.schedules)
            this.schedules.set(schedule.date, schedule);
        for (const goal of data.goals)
            this.goals.set(goal.id, goal);
        for (const note of data.notes)
            this.notes.set(note.id, note);
        for (const list of data.lists)
            this.lists.set(list.id, list);
        for (const instance of data.listInstances)
            this.listInstances.set(instance.id, instance);
        for (const hi of data.habitInstances) {
            this.habitInstances.set(`${hi.habit_id}:${hi.date}`, hi);
        }
        for (const entry of data.journalEntries || []) {
            this.journalEntries.set(entry.id, entry);
        }
        for (const mood of data.moodCheckIns || []) {
            this.moodCheckIns.set(mood.id, mood);
        }
        for (const review of data.reviews || [])
            this.reviews.set(review.id, review);
        for (const w of data.fitnessWorkouts || [])
            this.fitnessWorkouts.set(w.id, w);
        for (const b of data.fitnessBodyweights || [])
            this.fitnessBodyweights.set(b.id, b);
        this.fitnessSettings = data.fitnessSettings?.[0] ?? null;
        this.preferences = data.preferences;
        this.stats = data.stats;
        // Import tombstones (merge, don't clear)
        if (data.tombstones?.tombstones) {
            await this.importTombstones(data.tombstones.tombstones);
        }
    }
    async clearAll() {
        this.tasks.clear();
        this.projects.clear();
        this.schedules.clear();
        this.goals.clear();
        this.notes.clear();
        this.lists.clear();
        this.listInstances.clear();
        this.habitInstances.clear();
        this.journalEntries.clear();
        this.moodCheckIns.clear();
        this.reviews.clear();
        this.fitnessWorkouts.clear();
        this.fitnessBodyweights.clear();
        this.fitnessSettings = null;
        this.pendingChanges.clear();
        this.tombstones.clear();
        this.lastSyncTime = null;
        this.preferences = createDefaultPreferences();
        this.stats = createDefaultStats();
    }
}
//# sourceMappingURL=memory.js.map